// Solo state exposed to block renderers via React context.
//
// Solo is a UI-only inspection mode: no part of the Song model changes
// when a block is soloed. The SoloContext carries the active soloed id
// plus a precomputed set of ids that should render un-dimmed (the
// ancestor chain + the soloed subtree). Renderers read these to toggle
// visual state; the play pipeline reads `soloId` separately.

import { createContext, useContext } from 'react';
import type { BlockId } from './types';

export type SoloOps = {
  /** The currently soloed block id, or `null` if nothing is soloed. */
  soloId: BlockId | null;
  /** Ids that should render un-dimmed when a solo is active. Empty set
   *  means either nothing is soloed or the soloed id is stale (stored
   *  id no longer appears in the tree) — in both cases, dim nothing. */
  visibleIds: Set<BlockId>;
  /** Toggle solo on `id`: clears if already soloed; otherwise replaces
   *  any previous solo (radio semantics — one solo at a time). */
  toggle: (id: BlockId) => void;
  /** Clear the active solo. Safe to call with no solo active. */
  clear: () => void;
};

const NOOP_OPS: SoloOps = {
  soloId: null,
  visibleIds: new Set(),
  toggle: () => {},
  clear: () => {},
};

export const SoloContext = createContext<SoloOps>(NOOP_OPS);

export function useSoloContext(): SoloOps {
  return useContext(SoloContext);
}
